/**
 * FikoYT CAM — Toolpath hesaplama motoru (SIFIRDAN).
 * Gerçek 2.5D algoritmalar: raster/offset alan temizleme, kontur offset, peck delme döngüleri.
 * Tüm operasyonlar Z-katmanlı (stepdown) işlenir; CATIA Prismatic Machining semantiği.
 */
import { ICamOperasyon, ICamHareket, ICamToolpath, ITakim, ICamMakro, AXIAL_TIPLER, makroGuvZ, makroBul } from './fiko-cam.types';

type Makro = ICamMakro | undefined;

type P2 = { x: number; y: number };

// ───────────────────── 2D geometri yardımcıları ─────────────────────
function bbox(pts: P2[]): { minx: number; miny: number; maxx: number; maxy: number } {
  let minx = Infinity, miny = Infinity, maxx = -Infinity, maxy = -Infinity;
  for (const p of pts) { if (p.x < minx) minx = p.x; if (p.y < miny) miny = p.y; if (p.x > maxx) maxx = p.x; if (p.y > maxy) maxy = p.y; }
  return { minx, miny, maxx, maxy };
}
function alan(poly: P2[]): number { let a = 0; for (let i = 0, n = poly.length; i < n; i++) { const j = (i + 1) % n; a += poly[i].x * poly[j].y - poly[j].x * poly[i].y; } return a / 2; }
function noktaIcinde(poly: P2[], x: number, y: number): boolean {
  let ic = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i].x, yi = poly[i].y, xj = poly[j].x, yj = poly[j].y;
    if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) ic = !ic;
  }
  return ic;
}
/** Basit içe/dışa polygon offset (köşe-bazlı, dışbükey/hafif içbükey için yeterli). d>0 dışa. */
function offsetPoly(poly: P2[], d: number): P2[] {
  const n = poly.length; if (n < 3 || Math.abs(d) < 1e-6) return poly.map(p => ({ ...p }));
  const ccw = alan(poly) > 0; const s = ccw ? 1 : -1;   // CCW ise dış-normal +
  const out: P2[] = [];
  for (let i = 0; i < n; i++) {
    const a = poly[(i - 1 + n) % n], b = poly[i], c = poly[(i + 1) % n];
    const n1 = norm({ x: -(b.y - a.y), y: b.x - a.x }), n2 = norm({ x: -(c.y - b.y), y: c.x - b.x });
    const bis = norm({ x: (n1.x + n2.x) * s, y: (n1.y + n2.y) * s });
    const cosA = Math.max(0.3, n1.x * n2.x + n1.y * n2.y);   // keskin köşede patlamayı sınırla
    const len = d / Math.sqrt((1 + cosA) / 2);
    out.push({ x: b.x + bis.x * len, y: b.y + bis.y * len });
  }
  return out;
}
function norm(v: P2): P2 { const l = Math.hypot(v.x, v.y) || 1; return { x: v.x / l, y: v.y / l }; }

// ── OCCT B-Rep EXACT offset köprüsü (Direktif B: her şey B-Rep) ──
export type CamExactOffsetFn = (poly: P2[], dIce: number) => P2[] | null;
let camExactOffset: CamExactOffsetFn | null = null;
/** OCCT kernel köprüsünü bağlar (yönetici çağırır) — toolpath offset'leri topoloji-seviyesi exact olur; kernel yoksa JS fallback sürer. */
export function camExactOffsetBagla(fn: CamExactOffsetFn | null): void { camExactOffset = fn; }
/** Offset (d>0 = İÇE): önce OCCT B-Rep exact — AMA yalnızca KATI doğrulamadan geçerse (aksi halde kanıtlanmış JS offsetPoly).
 * OCCT serbest-wire offset'i her zaman güvenilir değil → sonuç yanlışsa toolpath'i ASLA bozmasın: JS'e düş. */
function offsetPolyX(poly: P2[], d: number): P2[] {
  if (camExactOffset && poly.length >= 3 && Math.abs(d) > 1e-6) {
    try {
      const o = camExactOffset(poly, d);
      if (o && o.length >= 3 && offsetGecerliMi(poly, o, d)) return o;
    } catch { }
  }
  return offsetPoly(poly, d);
}
/** OCCT offset sonucu sağlam mı: winding korundu + doğru yön (içe küçülür/dışa büyür) + bbox tutarlı + kendini kesmiyor. */
function offsetGecerliMi(kaynak: P2[], o: P2[], d: number): boolean {
  const a0 = alan(kaynak), a1 = alan(o);
  if (Math.abs(a1) < 1e-4) return false;
  if (Math.sign(a1) !== Math.sign(a0)) return false;              // winding döndü (bozuk)
  if (d > 0 ? Math.abs(a1) >= Math.abs(a0) : Math.abs(a1) <= Math.abs(a0)) return false;   // yanlış yön
  if (kendiniKesiyorMu(o)) return false;                          // self-intersection → geri dönme
  const b0 = bbox(kaynak), b1 = bbox(o);
  if (d > 0) { if (b1.minx < b0.minx - 0.01 || b1.maxx > b0.maxx + 0.01 || b1.miny < b0.miny - 0.01 || b1.maxy > b0.maxy + 0.01) return false; }   // içe: orijinali aşamaz
  else { if (b1.minx > b0.minx + 0.01 || b1.maxx < b0.maxx - 0.01 || b1.miny > b0.miny + 0.01 || b1.maxy < b0.maxy - 0.01) return false; }          // dışa: orijinali kapsamalı
  return true;
}
/** İki segment (uç-hariç) kesişiyor mu — self-intersection tespiti için. */
function segKes(p1: P2, p2: P2, p3: P2, p4: P2): boolean {
  const d1x = p2.x - p1.x, d1y = p2.y - p1.y, d2x = p4.x - p3.x, d2y = p4.y - p3.y;
  const den = d1x * d2y - d1y * d2x; if (Math.abs(den) < 1e-9) return false;
  const t = ((p3.x - p1.x) * d2y - (p3.y - p1.y) * d2x) / den;
  const u = ((p3.x - p1.x) * d1y - (p3.y - p1.y) * d1x) / den;
  return t > 1e-6 && t < 1 - 1e-6 && u > 1e-6 && u < 1 - 1e-6;
}
/** Kapalı polygon kendini kesiyor mu (geçersiz offset = takım geri dönüp tekrar geçer). */
function kendiniKesiyorMu(poly: P2[]): boolean {
  const n = poly.length; if (n < 4) return false;
  for (let i = 0; i < n; i++) {
    const a1 = poly[i], a2 = poly[(i + 1) % n];
    for (let j = i + 2; j < n; j++) {
      if (i === 0 && j === n - 1) continue;   // kapalı komşu segmenti atla
      if (segKes(a1, a2, poly[j], poly[(j + 1) % n])) return true;
    }
  }
  return false;
}
/** Per-kenar offset (Open Pocket Hard/Soft): ofsetler[i] = kenar i'nin dış-normal ötelemesi (+dışa = soft, -içe = hard). */
function offsetPoly2(poly: P2[], ofsetler: number[]): P2[] {
  const n = poly.length; if (n < 3) return poly.map(p => ({ ...p }));
  const s = alan(poly) > 0 ? 1 : -1;
  const cizgi: { p: P2; d: P2 }[] = [];   // her kenarın offset edilmiş çizgisi (nokta + birim yön)
  for (let i = 0; i < n; i++) {
    const a = poly[i], b = poly[(i + 1) % n];
    const d = norm({ x: b.x - a.x, y: b.y - a.y });
    const dn = { x: d.y * s, y: -d.x * s };   // dış normal
    const o = ofsetler[i] || 0;
    cizgi.push({ p: { x: a.x + dn.x * o, y: a.y + dn.y * o }, d });
  }
  const out: P2[] = [];
  for (let i = 0; i < n; i++) {
    const l1 = cizgi[(i - 1 + n) % n], l2 = cizgi[i];
    const det = l1.d.x * (-l2.d.y) - l1.d.y * (-l2.d.x);
    if (Math.abs(det) < 1e-9) { out.push({ ...l2.p }); continue; }   // paralel → çizgi başını al
    const dx = l2.p.x - l1.p.x, dy = l2.p.y - l1.p.y;
    const t = (dx * (-l2.d.y) - dy * (-l2.d.x)) / det;
    out.push({ x: l1.p.x + l1.d.x * t, y: l1.p.y + l1.d.y * t });
  }
  return out;
}
/** Bir taramayı (yatay y) polygon'la kes → girdi/çıktı x aralıkları. */
function scanlineKes(poly: P2[], y: number): [number, number][] {
  const xs: number[] = [];
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const yi = poly[i].y, yj = poly[j].y;
    if ((yi <= y && yj > y) || (yj <= y && yi > y)) { const t = (y - yi) / (yj - yi); xs.push(poly[i].x + t * (poly[j].x - poly[i].x)); }
  }
  xs.sort((a, b) => a - b);
  const out: [number, number][] = []; for (let i = 0; i + 1 < xs.length; i += 2) out.push([xs[i], xs[i + 1]]);
  return out;
}

/** Köşe yuvarlama (CATIA Type of Contour): her köşeye R sınırlı yumuşak geçiş (kuadratik) enjekte eder. */
function koseYuvarla(yol: P2[], R: number, kapali: boolean): P2[] {
  const n = yol.length; if (n < 3 || R < 1e-3) return yol.map(p => ({ ...p }));
  const out: P2[] = [];
  const bas = kapali ? 0 : 1, son = kapali ? n : n - 1;
  if (!kapali) out.push({ ...yol[0] });
  for (let i = bas; i < son; i++) {
    const p = yol[(i - 1 + n) % n], v = yol[i], q = yol[(i + 1) % n];
    const din = norm({ x: v.x - p.x, y: v.y - p.y }), dout = norm({ x: q.x - v.x, y: q.y - v.y });
    const lin = Math.hypot(v.x - p.x, v.y - p.y), lout = Math.hypot(q.x - v.x, q.y - v.y);
    const trim = Math.min(R, lin * 0.45, lout * 0.45);
    if (trim < 1e-3 || (din.x * dout.x + din.y * dout.y) > 0.9995) { out.push({ ...v }); continue; }   // düz veya çok kısa → keskin
    const a = { x: v.x - din.x * trim, y: v.y - din.y * trim }, b = { x: v.x + dout.x * trim, y: v.y + dout.y * trim };
    out.push(a);
    const K = 5;
    for (let k = 1; k < K; k++) { const t = k / K, u = 1 - t; out.push({ x: u * u * a.x + 2 * u * t * v.x + t * t * b.x, y: u * u * a.y + 2 * u * t * v.y + t * t * b.y }); }
    out.push(b);
  }
  if (!kapali) out.push({ ...yol[n - 1] });
  return out;
}
/** Strateji köseTip'ine göre köşe yuvarlama yarıçapı (0 = keskin/angular). */
function koseYaricap(s: any, r: number): number {
  const k = s.koseTip || 'optimized';
  if (k === 'angular') return 0;
  if (k === 'forcedCircular') return s.koseR || r * 0.4;
  if (k === 'circular') return s.koseR || r * 0.3;
  return s.koseR || r * 0.15;   // optimized: hafif yumuşatma
}

/** Aralık çıkarma (Open Pocket ada-dolaşma): base aralıklarından cikar aralıklarını siler. */
function aralikCikar(base: [number, number][], cikar: [number, number][]): [number, number][] {
  let res: [number, number][] = base.map(x => [x[0], x[1]]);
  for (const [cs, ce] of cikar) {
    const yeni: [number, number][] = [];
    for (const [bs, be] of res) {
      if (ce <= bs || cs >= be) { yeni.push([bs, be]); continue; }   // örtüşme yok
      if (cs > bs) yeni.push([bs, cs]);   // sol kalan
      if (ce < be) yeni.push([ce, be]);   // sağ kalan
    }
    res = yeni;
  }
  return res.filter(([s, e]) => e - s > 0.05);
}

// ───────────────────── Toolpath builder ─────────────────────
export class FikoCamToolpath {
  /** Bir operasyonun toolpath'ini hesaplar. */
  static hesapla(op: ICamOperasyon, takim: ITakim | undefined): ICamToolpath {
    const h: ICamHareket[] = [];
    const axialOp = AXIAL_TIPLER.includes(op.tip as any);
    // GÜVENLİK: freze op'unda geçersiz takımla offset üretmek parça bozar → toolpath REDDEDİLİR (sessiz 6mm varsayımı yok)
    if (!axialOp && (!takim || !(takim.cap > 0.1))) {
      return { opId: op.id, hareketler: [], uzunluk: 0, sure: 0, uyarilar: ['Geçerli takım atanmamış (Ø' + (takim?.cap ?? '—') + ') — toolpath üretilmedi, operasyona takım atayın'] };
    }
    const uyarilar: string[] = [];
    if (axialOp && !takim) uyarilar.push('Takım atanmamış — delme yolu varsayılan Ø6 ile üretildi');
    const r = Math.max(0.05, (takim?.cap || 6) / 2);
    const g = op.geometri, s = op.strateji, v = op.hizlar;
    const guvZ = makroGuvZ(op, (g.ustZ ?? 0) + 10);
    // CATIA offsets: üst↓, taban↑
    const ustZ = (g.ustZ ?? 0) - (g.ofsetUst || 0), altZ = (g.altZ ?? ((g.ustZ ?? 0) - (g.derinlik ?? 10))) + (g.ofsetTaban || 0);
    const fc = v.ilerleme || 200, fp = v.dalmaIlerleme || Math.max(50, fc * 0.4);
    const app = makroBul(op, 'approach'), ret = makroBul(op, 'retract');
    const rl = makroBul(op, 'returnLevel'), rb = makroBul(op, 'returnBetween');   // seviye-içi (talaş/pas arası) ve seviyeler-arası geçiş
    const la = makroBul(op, 'linkApproach');   // ada/bölge-arası bağlantı geçişi

    if (axialOp) { this.axial(h, op, takim, guvZ, ustZ, altZ, fc, fp); }
    else if (op.tip === 'facing') this.facing(h, g.konturlar?.[0] || this.stokKontur(g), r, s, ustZ, guvZ, fc, fp, app, ret);
    else if (op.tip === 'pocketing') {
      const altZf = g.tabanAcik ? altZ - (g.breakthrough ?? r) : altZ;           // soft bottom: breakthrough payı kadar altZ'yi geç
      const overhang = s.overhang ? (r * 2) * s.overhang / 100 : 0;              // soft kenar dışa taşma (% tool dia)
      const ff = v.finisIlerleme || fc;   // finishing pası feed
      this.pocket(h, g.konturlar || [], g.adalar || [], r, s, ustZ, altZf, guvZ, fc, fp, app, ret, g.acikKenarlar, overhang, rl, rb, la, ff);
    }
    else if (op.tip === 'contouring' || op.tip === 'curveFollow') { const altZc = g.altKosul === 'soft' ? altZ - (g.breakthrough ?? r) : altZ; this.contour(h, g.konturlar?.[0] || [], r, s, ustZ, altZc, guvZ, fc, fp, op.tip === 'curveFollow', app, ret); }
    else if (op.tip === 'pointToPoint') this.pointToPoint(h, g.noktalar || [], guvZ, fc);
    else if (op.tip === 'circularMill') this.circular(h, g.konturlar?.[0] || [], r, s, ustZ, altZ, guvZ, fc, fp, app, ret);

    this.toolAxisDonustur(h, op);   // CATIA Tool Axis (3+2): frame-local toolpath'i world'e döndür + IJK

    const uz = this.uzunluk(h); const sure = this.sure(h, fc);
    return { opId: op.id, hareketler: h, uzunluk: uz, sure, uyarilar: uyarilar.length ? uyarilar : undefined };
  }

  private static stokKontur(g: any): P2[] { return [{ x: -50, y: -50 }, { x: 50, y: -50 }, { x: 50, y: 50 }, { x: -50, y: 50 }]; }
  /** Ada güvenli DIŞA büyütme: OCCT exact → JS offsetPoly → per-kenar offsetPoly2 → bbox kutusu.
   * Her aday alan-büyüme + self-intersection ile doğrulanır; hiçbiri geçmezse bbox (fazla malzeme bırakır ama takım ADAYA ASLA GİRMEZ). */
  private static adaBuyut(a: P2[], d: number): P2[] {
    if (!a || a.length < 3) return [];
    const A0 = Math.abs(alan(a));
    const dogru = (o: P2[]) => o.length >= 3 && Math.abs(alan(o)) >= A0 && !kendiniKesiyorMu(o);
    const x = camExactOffset ? (() => { try { return camExactOffset!(a, -d); } catch { return null; } })() : null;
    if (x && dogru(x)) return x;
    const o1 = offsetPoly(a, -d); if (dogru(o1)) return o1;
    const o2 = offsetPoly2(a, a.map(() => d)); if (dogru(o2)) return o2;   // offsetPoly2: + = dışa
    const bb = bbox(a);
    return [{ x: bb.minx - d, y: bb.miny - d }, { x: bb.maxx + d, y: bb.miny - d }, { x: bb.maxx + d, y: bb.maxy + d }, { x: bb.minx - d, y: bb.maxy + d }];
  }
  /** CATIA Radial Mode → etkin yanal hatve (mm). */
  private static effYanal(s: any, r: number): number {
    const dia = r * 2;
    if (s.radialMod === 'pctTool' || s.radialMod === 'toolRatio') return Math.max(0.1, dia * (s.yanalHatveYuzde || 50) / 100);
    return s.yanalHatve || dia * 0.6;
  }
  /** CATIA Axial Mode → etkin eksenel hatve (mm). */
  private static effEksenel(s: any, ustZ: number, altZ: number, r: number): number {
    if (s.axialMod === 'numLevels' || s.axialMod === 'numLevelsNoTop') { const n = Math.max(1, Math.round(s.seviyeSayisi || 1)); return Math.max(0.1, (ustZ - altZ) / n); }
    return Math.max(0.1, s.eksenelHatve || r);
  }

  // ───────────────────── Makro giriş/çıkış enjeksiyonu (CATIA Approach/Retract) ─────────────────────
  /**
   * Malzemeye dalış hareketi. Approach makro tipine göre dik plunge / rampa / helis / ön-delik üretir.
   * ustZ→hedefZ arası iner; takım (x,y,hedefZ)'de kalır. yonAci = rampanın yatay yön açısı (rad).
   */
  private static girisYap(h: ICamHareket[], app: Makro, r: number, x: number, y: number, ustZ: number, hedefZ: number, fp: number, yonAci = 0): void {
    const i0 = h.length;
    this.girisYapCore(h, app, r, x, y, ustZ, hedefZ, fp, yonAci);
    for (let i = i0; i < h.length; i++) if (!h[i].mk) h[i].mk = 'app';   // 3D/2D makro görselleştirme için yaklaşma hareketlerini işaretle
  }
  private static girisYapCore(h: ICamHareket[], app: Makro, r: number, x: number, y: number, ustZ: number, hedefZ: number, fp: number, yonAci = 0): void {
    if (app?.aktif && app.mod === 'custom' && app.adimlar?.length) { this.customGiris(h, app, r, x, y, ustZ, hedefZ, fp, yonAci); return; }   // CATIA Build by user
    const tip = app?.aktif ? app.hareketTip : undefined;
    if ((ustZ - hedefZ) <= 1e-6 || !tip || tip === 'dikine' || tip === 'on') { h.push({ tip: 'plunge', x, y, z: hedefZ, f: fp }); return; }
    // Makro hareketini (rampa/helis/yay) malzeme yakınına sınırla: yukarısı havada rapid; sadece engagement yüksekliği kadar makro motion
    // (yoksa merkez raster/facing güvenlik düzleminden ~70mm helis/rampa üretir → havada gereksiz hareket).
    const makroH = Math.max(2, r * 1.5);
    if (ustZ - hedefZ > makroH) { const fp0 = hedefZ + makroH; h.push({ tip: 'rapid', x, y, z: fp0 }); ustZ = fp0; }
    const dz = ustZ - hedefZ;

    if (tip === 'rampa') {
      const a = Math.max(0.5, app?.aci || 3) * Math.PI / 180;
      const L = Math.max(1, app?.mesafe || r * 3);             // rampa yatay uzunluğu
      const segDz = Math.max(1e-3, Math.tan(a) * L);           // bir gidiş-segmentinde inilen
      const dirx = Math.cos(yonAci), diry = Math.sin(yonAci);
      h.push({ tip: 'rapid', x, y, z: ustZ });   // malzeme üstüne boşta in
      let z = ustZ, yon = 1, cx = x, cy = y, guard = 0;
      while (z > hedefZ + 1e-6 && guard++ < 600) {
        const nz = Math.max(hedefZ, z - segDz);
        cx += dirx * L * yon; cy += diry * L * yon;
        h.push({ tip: 'plunge', x: cx, y: cy, z: nz, f: fp });
        z = nz; yon = -yon;
      }
      h.push({ tip: 'feed', x, y, z: hedefZ, f: fp });          // giriş merkezine dön
      return;
    }

    if (tip === 'helis') {
      const rad = Math.max(0.5, app?.yaricap || r * 0.9);
      const a = Math.max(0.5, app?.aci || 3) * Math.PI / 180;
      const turDz = Math.max(1e-3, 2 * Math.PI * rad * Math.tan(a));   // bir turda inilen Z
      const tur = Math.max(1, Math.ceil(dz / turDz));
      const N = 24 * tur;
      h.push({ tip: 'rapid', x: x + rad, y, z: ustZ });
      for (let i = 1; i <= N; i++) {
        const t = i / N, ang = t * tur * 2 * Math.PI;
        h.push({ tip: 'plunge', x: x + Math.cos(ang) * rad, y: y + Math.sin(ang) * rad, z: ustZ - dz * t, f: fp });
      }
      h.push({ tip: 'feed', x, y, z: hedefZ, f: fp });          // merkeze gel
      return;
    }

    // yay vb. → dik fallback (teğet yay XY yanaşması contour'da uygulanır)
    h.push({ tip: 'plunge', x, y, z: hedefZ, f: fp });
  }

  /** CATIA "Build by user" özel YAKLAŞMA: adım dizisini (eksenel/teğet/rampa/helis/düzleme) yola çevir, engagement (x,y,hedefZ)'de biter. */
  private static customGiris(h: ICamHareket[], app: Makro, r: number, x: number, y: number, ustZ: number, hedefZ: number, fp: number, yonAci: number): void {
    const ad = app.adimlar || [];
    const dirx = Math.cos(yonAci), diry = Math.sin(yonAci);
    let totU = 0, totW = 0;   // toplam yatay + toplam dikey iniş → diziyi hedefZ'ye sabitle (havada feed-plunge önle)
    for (const s of ad) {
      if (s.tip === 'teget' || s.tip === 'rampa') totU += Math.max(0, s.mesafe || 0);
      if (s.tip === 'eksenel' || s.tip === 'helis') totW += Math.max(0.1, s.mesafe || 1);
      else if (s.tip === 'rampa') totW += Math.tan(Math.max(0.5, s.aci || 3) * Math.PI / 180) * Math.max(0.1, s.mesafe || 1);
      else if (s.tip === 'duzleme') totW += 3;
    }
    const px = (cu: number) => x - (totU - cu) * dirx, py = (cu: number) => y - (totU - cu) * diry;
    const baslaZ = hedefZ + totW;   // feed plane: dizi tam hedefZ'de biter; üstü rapid (havada feed yok)
    let cu = 0, cz = baslaZ;
    h.push({ tip: 'rapid', x: px(0), y: py(0), z: Math.max(ustZ, baslaZ) });   // güvenli yükseklikten
    if (baslaZ < ustZ - 1e-3) h.push({ tip: 'rapid', x: px(0), y: py(0), z: baslaZ });   // feed plane'e in (rapid, havada)
    for (const s of ad) {
      if (s.tip === 'eksenel') { cz -= Math.max(0.1, s.mesafe || 1); h.push({ tip: 'plunge', x: px(cu), y: py(cu), z: cz, f: fp }); }
      else if (s.tip === 'duzleme') { cz -= 3; h.push({ tip: 'plunge', x: px(cu), y: py(cu), z: cz, f: fp }); }   // approach: hedefZ'ye sabit (nominal iniş)
      else if (s.tip === 'teget') { cu += Math.max(0.1, s.mesafe || 1); h.push({ tip: 'feed', x: px(cu), y: py(cu), z: cz, f: fp }); }
      else if (s.tip === 'rampa') { const L = Math.max(0.1, s.mesafe || 1); cu += L; cz -= Math.tan(Math.max(0.5, s.aci || 3) * Math.PI / 180) * L; h.push({ tip: 'plunge', x: px(cu), y: py(cu), z: cz, f: fp }); }
      else if (s.tip === 'helis') {
        const cx = px(cu), cy = py(cu), rad = Math.max(0.5, s.yaricap || r * 0.9), dzz = Math.max(0.1, s.mesafe || 2);
        const a = Math.max(0.5, s.aci || 3) * Math.PI / 180, turDz = Math.max(1e-3, 2 * Math.PI * rad * Math.tan(a)), tur = Math.max(1, Math.ceil(dzz / turDz)), N = 24 * tur;
        for (let i = 1; i <= N; i++) { const t = i / N; h.push({ tip: 'plunge', x: cx + Math.cos(t * tur * 2 * Math.PI) * rad, y: cy + Math.sin(t * tur * 2 * Math.PI) * rad, z: cz - dzz * t, f: fp }); }
        cz -= dzz; h.push({ tip: 'feed', x: cx, y: cy, z: cz, f: fp });
      }
    }
    h.push({ tip: 'feed', x, y, z: hedefZ, f: fp });   // engagement noktasına son bağlayıcı
  }
  /** CATIA "Build by user" özel GERİ ÇEKME: son kesim noktasından adım dizisiyle yukarı/dışarı çıkar, sonra guvZ. */
  private static customCikis(h: ICamHareket[], ret: Makro, guvZ: number, fc: number): void {
    const son = h[h.length - 1]; if (!son) return;
    const ad = ret.adimlar || [];
    let cx = son.x, cy = son.y, cz = son.z;
    for (const s of ad) {
      if (s.tip === 'eksenel') { cz += Math.max(0.1, s.mesafe || 1); h.push({ tip: 'feed', x: cx, y: cy, z: cz, f: fc }); }
      else if (s.tip === 'duzleme') { cz = s.hedefZ ?? guvZ; h.push({ tip: 'feed', x: cx, y: cy, z: cz, f: fc }); }
      else if (s.tip === 'teget') { cx += Math.max(0.1, s.mesafe || 1); h.push({ tip: 'feed', x: cx, y: cy, z: cz, f: fc }); }
      else if (s.tip === 'rampa') { const L = Math.max(0.1, s.mesafe || 1); cx += L; cz += Math.tan(Math.max(0.5, s.aci || 3) * Math.PI / 180) * L; h.push({ tip: 'feed', x: cx, y: cy, z: cz, f: fc }); }
      else if (s.tip === 'helis') {
        const rad = Math.max(0.5, s.yaricap || 2), dzz = Math.max(0.1, s.mesafe || 2);
        const a = Math.max(0.5, s.aci || 3) * Math.PI / 180, turDz = Math.max(1e-3, 2 * Math.PI * rad * Math.tan(a)), tur = Math.max(1, Math.ceil(dzz / turDz)), N = 24 * tur;
        for (let i = 1; i <= N; i++) { const t = i / N; h.push({ tip: 'feed', x: cx + Math.cos(t * tur * 2 * Math.PI) * rad, y: cy + Math.sin(t * tur * 2 * Math.PI) * rad, z: cz + dzz * t, f: fc }); }
        cz += dzz;
      }
    }
    h.push({ tip: 'retract', x: cx, y: cy, z: guvZ });
  }

  /** Geri çekme. Retract makro teğetsel/rampalı ise (yay/rampa + mesafe) hafif XY uzaklaşma ekler, sonra guvZ'ye çıkar. */
  private static cikisYap(h: ICamHareket[], ret: Makro, guvZ: number, fc: number): void {
    const i0 = h.length;
    this.cikisYapCore(h, ret, guvZ, fc);
    for (let i = i0; i < h.length; i++) if (!h[i].mk) h[i].mk = 'ret';   // geri-çekme hareketlerini işaretle
  }
  private static cikisYapCore(h: ICamHareket[], ret: Makro, guvZ: number, fc: number): void {
    if (!h.length) return;
    if (ret?.aktif && ret.mod === 'custom' && ret.adimlar?.length) { this.customCikis(h, ret, guvZ, fc); return; }   // CATIA Build by user
    const son = h[h.length - 1];
    if (ret?.aktif && (ret.hareketTip === 'yay' || ret.hareketTip === 'rampa') && (ret.mesafe || ret.yaricap)) {
      const d = ret.mesafe || ret.yaricap || 2;
      h.push({ tip: 'feed', x: son.x + d, y: son.y, z: son.z, f: fc });   // teğetsel kaçış (XY)
    }
    const t = h[h.length - 1];
    h.push({ tip: 'retract', x: t.x, y: t.y, z: guvZ });
  }

  /** FACING — bbox üzerinde raster (zigzag) paralel paslar, tek Z (üst yüz). */
  private static facing(h: ICamHareket[], poly: P2[], r: number, s: any, z: number, guvZ: number, fc: number, fp: number, app?: Makro, ret?: Makro): void {
    if (poly.length < 3) return;
    const bb = bbox(poly); const step = this.effYanal(s, r) || Math.max(1, r * 1.6);
    const aci = (s.rasterAci || 0) * Math.PI / 180; // 0 = X yönü
    let yon = 1, ilk = true;
    for (let y = bb.miny + step / 2; y <= bb.maxy; y += step) {
      const segs = scanlineKes(poly, y); if (!segs.length) continue;
      const seg = yon > 0 ? segs[0] : segs[segs.length - 1];
      const [x0, x1] = yon > 0 ? [seg[0] + r, seg[1] - r] : [seg[1] - r, seg[0] + r];
      if (ilk) { h.push({ tip: 'rapid', x: x0, y, z: guvZ }); this.girisYap(h, app, r, x0, y, guvZ, z, fp, aci); ilk = false; }
      else { h.push({ tip: 'feed', x: x0, y, z, f: fc }); }
      h.push({ tip: 'feed', x: x1, y, z, f: fc });
      yon = -yon;
    }
    if (!ilk) this.cikisYap(h, ret, guvZ, fc);
  }

  /** POCKET — Z katmanları (stepdown), her katta Tool Path Style'a göre raster/helical temizleme + finishing. */
  private static pocket(h: ICamHareket[], konturlar: P2[][], adalar: P2[][], r: number, s: any, ustZ: number, altZ: number, guvZ: number, fc: number, fp: number, app?: Makro, ret?: Makro, acik?: number[], overhang = 0, rl?: Makro, rb?: Makro, la?: Makro, ff = fc): void {
    const dis = konturlar[0]; if (!dis || dis.length < 3) return;
    const finis = (s.finisPayi || 0) + (s.tabanPayi !== undefined ? 0 : 0);   // radial leave
    // Open Pocket: soft (açık) kenarlarda takım dışarı taşar (+overhang), hard kenarlarda r içe.
    // offsetPoly2 (kenar-kesişim) keskin köşelerde offsetPoly'nin dışa patlamasını da önler (takım duvarı aşmaz).
    const acikSet = acik && acik.length ? new Set(acik) : null;
    const calismaPoly = offsetPoly2(dis, dis.map((_, i) => acikSet?.has(i) ? +overhang : -(r + finis)));
    // Adalar (island): takım yarıçapı + finiş kadar GÜVENLE dışa büyütülür (asla içe küçültülmez — takım adaya giremez).
    const adaOff = (adalar || []).map(a => this.adaBuyut(a, r + finis)).filter(a => a.length >= 3);
    const stepZ = this.effEksenel(s, ustZ, altZ, r);
    const tabanPay = s.finisMod === 'tabanda' ? (s.tabanPayi || 0.3) : 0;
    const katlar: number[] = []; for (let z = ustZ - stepZ; z > altZ + tabanPay + 1e-6; z -= stepZ) katlar.push(z); katlar.push(altZ + tabanPay);
    // Ada (island) varken helical offset adayı yok sayar (takım adayı keser) → raster'a düş (katRaster aralikCikar ile adayı güvenle atlar).
    const helical = !acikSet && !adaOff.length && ((s.toolPathStyle === 'helicalIn') || s.strateji === 'offsetIn' || s.strateji === 'offsetOut');
    // Kat-arası giriş: ilk kat malzeme üstünden (ustZ), sonrakiler bir önceki kat Z'sinden iner (havadaki iniş rapid ile)
    // returnBetween (seviyeler arası): ara katlarda tam güvenliğe çıkma — kat üstü + jump kadar kısa sıçra; son kat guvZ'ye.
    let oncekiZ = ustZ;
    for (let i = 0; i < katlar.length; i++) {
      const z = katlar[i], sonKat = i === katlar.length - 1;
      const cikZ = sonKat ? guvZ : (rb?.aktif ? z + Math.max(0.5, rb.jumpYuksek ?? rb.mesafe ?? 2) : guvZ);
      const sc = (sonKat && s.avoidScallops) ? 0.5 : 1;   // avoidScallops: son kat daha sık raster (kalıntı tepe önle)
      if (helical) this.katOffset(h, dis, r, finis, s, z, oncekiZ, guvZ, fc, fp, app, ret, cikZ);
      else this.katRaster(h, calismaPoly, r, s, z, oncekiZ, guvZ, fc, fp, s.toolPathStyle === 'oneWay', app, ret, rl, cikZ, adaOff, la, sc);
      // ada finishing: her ada etrafında kontur pası (takım ada sınırına teğet)
      for (const a of adaOff) { h.push({ tip: 'rapid', x: a[0].x, y: a[0].y, z: guvZ }); h.push({ tip: 'plunge', x: a[0].x, y: a[0].y, z, f: fp }); for (let i = 1; i <= a.length; i++) { const p = a[i % a.length]; h.push({ tip: 'feed', x: p.x, y: p.y, z, f: fc }); } this.cikisYap(h, ret, cikZ, fc); }
      oncekiZ = z;
    }
    // Finishing: tabanda/tam kontur finiş pası (kalan radial+taban payını al, ayrı finiş feed'i ff)
    if (s.finisMod === 'tabanda' || s.finisTam) {
      const fy = offsetPoly2(dis, dis.map(() => -r));   // duvara teğet kontur (yan finiş)
      h.push({ tip: 'rapid', x: fy[0].x, y: fy[0].y, z: guvZ }); this.girisYap(h, app, r, fy[0].x, fy[0].y, guvZ, altZ, fp);
      for (let i = 1; i <= fy.length; i++) { const p = fy[i % fy.length]; h.push({ tip: 'feed', x: p.x, y: p.y, z: altZ, f: ff }); }
      this.cikisYap(h, ret, guvZ, fc);
      // finisTam: ek taban temizleme rasterı (taban payını da al)
      if (s.finisTam) this.katRaster(h, offsetPoly2(dis, dis.map(() => -(r))), r, s, altZ, guvZ, guvZ, ff, fp, false, app, ret, rl, guvZ, adaOff, la, 1);
    }
  }
  private static katRaster(h: ICamHareket[], poly: P2[], r: number, s: any, z: number, ustZ: number, guvZ: number, fc: number, fp: number, oneWay = false, app?: Makro, ret?: Makro, rl?: Makro, cikZ = guvZ, adaOff?: P2[][], la?: Makro, stepCarpan = 1): void {
    if (poly.length < 3) return; const bb = bbox(poly); const step = (this.effYanal(s, r) || Math.max(1, r * 1.4)) * stepCarpan;   // avoidScallops: son katta daha sık (stepCarpan<1)
    const rasterAci = (s.rasterAci || 0) * Math.PI / 180;
    // returnLevel (talaş/pas arası): tam güvenliğe çıkma, kat+jump kadar kısa sıçra. linkApproach: ada/bölge-arası geçiş.
    const linkZ = rl?.aktif ? z + Math.max(0.2, rl.jumpYuksek ?? rl.mesafe ?? 2) : guvZ;
    const linkAdaZ = la?.aktif ? z + Math.max(0.2, la.jumpYuksek ?? la.mesafe ?? 2) : linkZ;
    let yon = 1, ilk = true;
    for (let y = bb.miny + step / 2; y <= bb.maxy - step / 2 + 1e-6; y += step) {
      let segs = scanlineKes(poly, y);
      if (adaOff?.length) for (const a of adaOff) segs = aralikCikar(segs, scanlineKes(a, y));   // ada bölgelerini çıkar (dolaş)
      if (!segs.length) continue;
      const sirali = yon > 0 ? segs : [...segs].reverse();
      let ilkParca = true;   // bu şeritteki ilk parça mı (ada böldüyse sonrakiler linkApproach)
      for (const seg of sirali) {
        const [x0, x1] = yon > 0 ? [seg[0], seg[1]] : [seg[1], seg[0]];
        if (ilk) { h.push({ tip: 'rapid', x: x0, y, z: guvZ }); this.girisYap(h, app, r, x0, y, ustZ, z, fp, rasterAci); ilk = false; }
        else { const lz = ilkParca ? linkZ : linkAdaZ; h.push({ tip: 'rapid', x: x0, y, z: lz }); h.push({ tip: 'plunge', x: x0, y, z, f: fp }); }   // şerit-arası / ada-arası linking
        h.push({ tip: 'feed', x: x1, y, z, f: fc });
        ilkParca = false;
      }
      if (!oneWay) yon = -yon;
    }
    if (!ilk) this.cikisYap(h, ret, cikZ, fc);
  }
  // Offset-in (helical/spiral) cep: kontur içe offsetlenir; merkeze kadar gider, KALAN merkez bölge raster ile temizlenir (ortada malzeme bırakmaz).
  private static katOffset(h: ICamHareket[], dis: P2[], r: number, finis: number, s: any, z: number, ustZ: number, guvZ: number, fc: number, fp: number, app?: Makro, ret?: Makro, cikZ = guvZ): void {
    const step = this.effYanal(s, r) || Math.max(1, r * 1.4); let d = r + finis; let ilk = true; let guard = 0;
    const disBB = bbox(dis); const sgn0 = Math.sign(alan(dis)) || 1;   // içe offset asla bu sınırı aşamaz / alanı işaret değiştiremez
    let sonYol: P2[] | null = null;
    while (guard++ < 400) {
      const yol = offsetPolyX(dis, d); if (yol.length < 3) break;   // d>0 = İÇE (OCCT exact → JS fallback)
      const ay = alan(yol);
      // offsetPoly merkezi geçince ters döner/patlar: alan işareti döndü ya da yok oldu → dur
      if (Math.sign(ay) !== sgn0 || Math.abs(ay) < 1e-3) break;
      // KENDİNİ KESEN offset (komşu kenarlar çakıştı) = takım geri dönüp tekrar geçer → bu offset GEÇERSİZ, dur (kalan raster temizler)
      if (kendiniKesiyorMu(yol)) break;
      const bb = bbox(yol);
      // geçerli içe offset yalnızca küçülür; orijinal bbox dışına taştıysa (köşe patlaması) dur
      if (bb.minx < disBB.minx - 1e-3 || bb.maxx > disBB.maxx + 1e-3 || bb.miny < disBB.miny - 1e-3 || bb.maxy > disBB.maxy + 1e-3) break;
      // kalan iç bölge bir adımdan küçükse offset durur → merkez raster temizler (erken durma yerine)
      if (bb.maxx - bb.minx < step * 0.8 && bb.maxy - bb.miny < step * 0.8) { sonYol = yol; break; }
      if (ilk) { h.push({ tip: 'rapid', x: yol[0].x, y: yol[0].y, z: guvZ }); this.girisYap(h, app, r, yol[0].x, yol[0].y, ustZ, z, fp); ilk = false; }
      else h.push({ tip: 'feed', x: yol[0].x, y: yol[0].y, z, f: fc });
      for (let i = 1; i <= yol.length; i++) { const p = yol[i % yol.length]; h.push({ tip: 'feed', x: p.x, y: p.y, z, f: fc }); }
      sonYol = yol;
      d += step;
    }
    if (!ilk) this.cikisYap(h, ret, cikZ, fc);
    // MERKEZ TEMİZLİĞİ: sadece offset'in ULAŞAMADIĞI gerçek kalan (son offset'ten bir adım daha içe) raster ile taranır — offset paslarının ÜSTÜNE binmez
    if (sonYol && sonYol.length >= 3) {
      let kalan = offsetPolyX(sonYol, step);   // son çizilen offset'ten bir adım içe = işlenmemiş çekirdek
      if (kalan.length >= 3 && !kendiniKesiyorMu(kalan) && Math.sign(alan(kalan)) === sgn0) {
        const mb = bbox(kalan);
        if (Math.min(mb.maxx - mb.minx, mb.maxy - mb.miny) > r) {   // kalan takım yarıçapından büyükse temizle (küçükse takım zaten kaplar)
          this.katRaster(h, kalan, r, s, z, guvZ, guvZ, fc, fp, false, app, ret, undefined, cikZ);
        }
      }
    }
  }

  /** CONTOUR — konturu takım yarıçapı kadar offsetleyip Z katlarında izle. acik=curve following. */
  private static contour(h: ICamHareket[], poly: P2[], r: number, s: any, ustZ: number, altZ: number, guvZ: number, fc: number, fp: number, acik: boolean, app?: Makro, ret?: Makro): void {
    if (poly.length < 2) return;
    // CATIA Tool Side: ic → takım içeride (d>0), dis → dışarıda (d<0), uzeri → takım merkezi kontur üzerinde (offset yok).
    const taraf = s.taraf || (s.yon === 'conventional' ? 'dis' : 'ic');
    const yon = taraf === 'dis' ? -1 : taraf === 'uzeri' ? 0 : 1;
    const dd = yon * (r + (s.finisPayi || 0));
    let yol = (acik || yon === 0) ? poly.map(p => ({ ...p })) : offsetPolyX(poly, dd);
    // konkav profilde offsetPoly kendini keserse (geri dönme) → kenar-bazlı offsetPoly2 fallback (offsetPoly2: +dışa/-içe → -dd)
    if (!acik && yon !== 0 && kendiniKesiyorMu(yol)) {
      const alt = offsetPoly2(poly, poly.map(() => -dd));
      if (alt.length >= 3 && !kendiniKesiyorMu(alt) && Math.sign(alan(alt)) === Math.sign(alan(poly))) yol = alt;
    }
    const koseR = koseYaricap(s, r); if (koseR > 0) yol = koseYuvarla(yol, koseR, !acik);   // köşe işleme (Type of Contour)
    const stepZ = this.effEksenel(s, ustZ, altZ, r);
    const katlar: number[] = []; for (let z = ustZ - stepZ; z > altZ + 1e-6; z -= stepZ) katlar.push(z); katlar.push(altZ);
    // teğet yay giriş/çıkış noktaları (profil ilk segmentine teğet, XY düzleminde)
    const yayGiris = app?.aktif && app.hareketTip === 'yay', yayCikis = ret?.aktif && ret.hareketTip === 'yay';
    const yayR = Math.max(0.5, (app?.yaricap || ret?.yaricap) || r);
    // Tabs (köprü): sadece kapalı konturun SON katında parça tutturma köprüleri bırak
    const sonZ = katlar[katlar.length - 1];
    const tabVar = !acik && s.tabAktif && (s.tabAdet || 0) > 0 && (s.tabGenislik || 0) > 0;
    // Makro/yay yönü takım tarafına göre: dış profilde malzeme-dışı = sağ normal, iç/cep'te = sol normal (yay malzemeye doğru girer)
    const dikYon = taraf === 'dis' ? 1 : -1;
    let oncekiZ = ustZ;
    for (const z of katlar) {
      const p0 = yol[0], p1 = yol[1 % yol.length];
      const teg = norm({ x: p1.x - p0.x, y: p1.y - p0.y });
      h.push({ tip: 'rapid', x: yol[0].x, y: yol[0].y, z: guvZ });
      this.girisYap(h, app, r, yol[0].x, yol[0].y, oncekiZ, z, fp, Math.atan2(teg.y, teg.x));
      if (yayGiris) { const c = this.yayNokta(p0, teg, yayR, true, dikYon); h.push({ tip: 'feed', x: c.x, y: c.y, z, f: fc }); h.push({ tip: 'feed', x: p0.x, y: p0.y, z, f: fc }); }
      if (tabVar && z === sonZ) {
        this.tabUygula(h, yol, z, z + Math.min(s.tabYukseklik || 1, ustZ - z + 0.001), s.tabAdet || 3, s.tabGenislik || 5, fc);
      } else {
        for (let i = 1; i < yol.length; i++) h.push({ tip: 'feed', x: yol[i].x, y: yol[i].y, z, f: fc });
        if (!acik) h.push({ tip: 'feed', x: yol[0].x, y: yol[0].y, z, f: fc });
      }
      if (yayCikis) { const c = this.yayNokta(p0, teg, yayR, false, dikYon); h.push({ tip: 'feed', x: c.x, y: c.y, z, f: fc }); }
      this.cikisYap(h, ret, guvZ, fc);
      oncekiZ = guvZ;
    }
  }
  /** Tabs/Bridges: kapalı yolu izlerken eşit aralıklı köprü bölgelerinde Z'yi tabZ'ye (tabandan yukarı) kaldırır — parça düşmez. */
  private static tabUygula(h: ICamHareket[], yol: P2[], z: number, tabZ: number, adet: number, gen: number, fc: number): void {
    const kapali = [...yol, yol[0]];
    const segLen: number[] = []; let toplam = 0;
    for (let i = 1; i < kapali.length; i++) { const d = Math.hypot(kapali[i].x - kapali[i - 1].x, kapali[i].y - kapali[i - 1].y); segLen.push(d); toplam += d; }
    if (toplam < 1e-6) { for (let i = 1; i < yol.length; i++) h.push({ tip: 'feed', x: yol[i].x, y: yol[i].y, z, f: fc }); h.push({ tip: 'feed', x: yol[0].x, y: yol[0].y, z, f: fc }); return; }
    const merkez: number[] = []; for (let k = 0; k < adet; k++) merkez.push((k + 0.5) / adet * toplam);
    const yari = Math.min(gen, toplam / adet * 0.8) / 2;
    const kopruMu = (sm: number) => merkez.some(cm => { let d = Math.abs(sm - cm); d = Math.min(d, toplam - d); return d < yari; });
    let s = 0;
    h.push({ tip: 'feed', x: kapali[0].x, y: kapali[0].y, z: kopruMu(0) ? tabZ : z, f: fc });
    for (let i = 1; i < kapali.length; i++) {
      const segL = segLen[i - 1]; const N = Math.max(1, Math.ceil(segL / 0.6));
      const ax = kapali[i - 1].x, ay = kapali[i - 1].y, bx = kapali[i].x, by = kapali[i].y;
      for (let j = 1; j <= N; j++) { const t = j / N; h.push({ tip: 'feed', x: ax + (bx - ax) * t, y: ay + (by - ay) * t, z: kopruMu(s + segL * t) ? tabZ : z, f: fc }); }
      s += segL;
    }
  }
  /** Teğet yay yardımcı noktası: profil noktasından teğete dik yönde yarıçap kadar açılım. dikYon=-1 sol (iç), +1 sağ (dış) — yay malzeme-dışından girer. */
  private static yayNokta(p: P2, teg: P2, R: number, giris: boolean, dikYon: number = -1): P2 {
    const dik = dikYon > 0 ? { x: teg.y, y: -teg.x } : { x: -teg.y, y: teg.x };   // dış→sağ normal, iç→sol normal
    const s = giris ? -1 : 1;
    return { x: p.x + teg.x * R * s + dik.x * R, y: p.y + teg.y * R * s + dik.y * R };
  }

  /** POINT TO POINT — noktalar arası feed hareketi (gravür/iz). */
  private static pointToPoint(h: ICamHareket[], pts: { x: number; y: number; z: number }[], guvZ: number, fc: number): void {
    if (!pts.length) return;
    h.push({ tip: 'rapid', x: pts[0].x, y: pts[0].y, z: guvZ });
    h.push({ tip: 'plunge', x: pts[0].x, y: pts[0].y, z: pts[0].z, f: fc });
    for (let i = 1; i < pts.length; i++) h.push({ tip: 'feed', x: pts[i].x, y: pts[i].y, z: pts[i].z, f: fc });
    h.push({ tip: 'retract', x: pts[pts.length - 1].x, y: pts[pts.length - 1].y, z: guvZ });
  }

  /** CIRCULAR MILL — kontur merkezinde helisel dairesel freze. */
  private static circular(h: ICamHareket[], poly: P2[], r: number, s: any, ustZ: number, altZ: number, guvZ: number, fc: number, fp: number, app?: Makro, ret?: Makro): void {
    if (poly.length < 3) return; const bb = bbox(poly);
    const cx = (bb.minx + bb.maxx) / 2, cy = (bb.miny + bb.maxy) / 2;
    const Rmax = Math.max(0, Math.min(bb.maxx - bb.minx, bb.maxy - bb.miny) / 2 - r);
    if (Rmax <= 0) return;
    const N = 48, stepZ = Math.max(0.1, s.eksenelHatve || r * 0.5);
    // radyal paslar (büyük delik için içten dışa): daireYolSayisi varsa çok pas
    const yolSay = Math.max(1, Math.round(s.daireYolSayisi || 1)), yolMes = s.daireYolMesafe || r * 1.2;
    const Rler: number[] = yolSay > 1 ? Array.from({ length: yolSay }, (_, i) => Rmax - (yolSay - 1 - i) * yolMes).filter(R => R > 0.1) : [Rmax];
    if ((s.daireMod || 'helical') === 'standard') {
      // Standard: Z katları, her katta tüm radyal daireler (düz daire)
      const katlar: number[] = []; for (let z = ustZ - stepZ; z > altZ + 1e-6; z -= stepZ) katlar.push(z); katlar.push(altZ);
      for (const z of katlar) for (const R of Rler) {
        h.push({ tip: 'rapid', x: cx + R, y: cy, z: guvZ }); this.girisYap(h, app, r, cx + R, cy, ustZ, z, fp);
        for (let i = 1; i <= N; i++) { const a = (i / N) * Math.PI * 2; h.push({ tip: 'feed', x: cx + Math.cos(a) * R, y: cy + Math.sin(a) * R, z, f: fc, arc: { cx, cy, cw: false } }); }
        this.cikisYap(h, ret, guvZ, fc);
      }
      return;
    }
    // Helical: dış R'de helisel iniş (mevcut), büyük delikse iç R'ler de helisel
    for (const R of Rler) {
      h.push({ tip: 'rapid', x: cx + R, y: cy, z: guvZ }); this.girisYap(h, app, r, cx + R, cy, guvZ, ustZ, fp);
      let z = ustZ;
      while (z > altZ + 1e-6) { for (let i = 1; i <= N; i++) { const a = (i / N) * Math.PI * 2; const zz = Math.max(altZ, z - stepZ * (i / N)); h.push({ tip: 'feed', x: cx + Math.cos(a) * R, y: cy + Math.sin(a) * R, z: zz, f: fc, arc: { cx, cy, cw: false } }); } z -= stepZ; }
      for (let i = 1; i <= N; i++) { const a = (i / N) * Math.PI * 2; h.push({ tip: 'feed', x: cx + Math.cos(a) * R, y: cy + Math.sin(a) * R, z: altZ, f: fc, arc: { cx, cy, cw: false } }); }
      this.cikisYap(h, ret, guvZ, fc);
    }
  }

  /** Delik noktalarını hazırla: deaktif (aktif=false) atla + sırala (girdiği sıra / en yakın komşu, baslangic'tan). */
  private static delikNoktalari(op: ICamOperasyon): { x: number; y: number; z: number }[] {
    let pts = (op.geometri.noktalar || []).filter(p => p.aktif !== false);
    if (op.strateji.noktaSirala === 'enYakin' && pts.length > 2) {
      const kalan = pts.slice(); const bas = kalan.findIndex(p => p.baslangic) ; const sirali = [kalan.splice(bas >= 0 ? bas : 0, 1)[0]];
      while (kalan.length) { const son = sirali[sirali.length - 1]; let mi = 0, md = Infinity; for (let i = 0; i < kalan.length; i++) { const d = (kalan[i].x - son.x) ** 2 + (kalan[i].y - son.y) ** 2; if (d < md) { md = d; mi = i; } } sirali.push(kalan.splice(mi, 1)[0]); }
      pts = sirali;
    } else { const bi = pts.findIndex(p => p.baslangic); if (bi > 0) pts = [pts[bi], ...pts.slice(0, bi), ...pts.slice(bi + 1)]; }
    return pts;
  }

  /** AXIAL — delme aileleri (standart/peck/break-chip/dwell) + özgün davranışlar (thread mill / t-slot / bara feed-out). */
  private static axial(h: ICamHareket[], op: ICamOperasyon, takim: ITakim | undefined, guvZ: number, ustZ: number, altZ: number, fc: number, fp: number): void {
    const pts = this.delikNoktalari(op); const s = op.strateji; const tip = op.tip;
    // Helisel freze tipleri (matkap DEĞİL — takım yana açılır): diş freze / T-kanal
    if (tip === 'threadMilling' || tip === 'threadNoTap') { this.threadMill(h, pts, op, takim, guvZ, ustZ, altZ, fc, fp); return; }
    if (tip === 'tslotting') { this.tslot(h, pts, op, takim, guvZ, ustZ, altZ, fc, fp); return; }
    const feedOut = tip === 'boring' || tip === 'reaming' || tip === 'boringChamfer' || tip === 'backBore';   // bara/rayba: kontrollü feed çıkış (duvar izi yok)
    const dongu: string = s.dongu || (tip === 'deepHole' ? 'peck' : tip === 'breakChips' ? 'breakChip' : 'standart');
    const peck0 = s.peckDerinlik || Math.max(1, (takim?.cap ?? 6) * 0.5);
    const dec = Math.max(0, Math.min(0.9, s.decrement || 0));               // deep hole: peck azaltma oranı
    const decLim = s.decrementLimit || Math.max(0.5, peck0 * 0.3);          // minimum peck
    const geri = s.geriCekme ?? 1;
    const through = s.breakthrough || 0;                                     // through-hole delip çıkma payı
    const fOut = op.hizlar.geriCekmeIlerleme || fc;
    for (const p of pts) {
      const top = p.z ?? ustZ; const dip = top - (op.geometri.derinlik ?? (top - altZ)) - through;   // breakthrough → dibi geç
      h.push({ tip: 'rapid', x: p.x, y: p.y, z: guvZ });
      h.push({ tip: 'rapid', x: p.x, y: p.y, z: top + 1 });
      if (dongu === 'peck' || dongu === 'breakChip') {
        let z = top, peck = peck0;
        while (z > dip + 1e-6) {
          const hedef = Math.max(dip, z - peck);
          h.push({ tip: 'plunge', x: p.x, y: p.y, z: hedef, f: fp });
          if (dongu === 'peck') h.push({ tip: 'retract', x: p.x, y: p.y, z: top + 1 });   // tam çık (talaş boşalt)
          else h.push({ tip: 'retract', x: p.x, y: p.y, z: hedef + geri });                // kısmi (talaş kır)
          z = hedef;
          if (dec > 0) peck = Math.max(decLim, peck * (1 - dec));                           // deep hole: derinleştikçe peck azalt
          if (dongu === 'peck' && z > dip + 1e-6) h.push({ tip: 'rapid', x: p.x, y: p.y, z: z + geri });
        }
      } else {
        h.push({ tip: 'plunge', x: p.x, y: p.y, z: dip, f: fp });
        if (dongu === 'dwell' || (s.dwellMod && s.dwellMod !== 'kapali')) h.push({ tip: 'feed', x: p.x, y: p.y, z: dip, f: 1 });   // dwell ~ sıfır ilerleme (bekleme)
      }
      if (feedOut) h.push({ tip: 'feed', x: p.x, y: p.y, z: top + 1, f: fOut });   // kontrollü çıkış (rapid değil)
      h.push({ tip: 'retract', x: p.x, y: p.y, z: guvZ });
    }
  }

  /** THREAD MILLING — her noktada merkeze dibe in, diş çapına yanaş, hatve adımlı helisel yukarı (iç diş). */
  private static threadMill(h: ICamHareket[], pts: { x: number; y: number; z: number }[], op: ICamOperasyon, takim: ITakim | undefined, guvZ: number, ustZ: number, altZ: number, fc: number, fp: number): void {
    const tcap = takim?.cap ?? 6;
    const hatve = takim?.hatve || op.strateji.peckDerinlik || 1.5;
    const nominal = op.geometri.cap || tcap * 2;
    const R = Math.max(0.3, nominal / 2 - tcap / 2);   // helis yarıçapı (diş dibi merkezi)
    const N = 36;
    for (const p of pts) {
      const top = p.z ?? ustZ; const dip = top - (op.geometri.derinlik ?? (top - altZ));
      const tur = Math.max(1, Math.ceil((top - dip) / hatve));
      const seg = N * tur;
      h.push({ tip: 'rapid', x: p.x, y: p.y, z: guvZ });
      h.push({ tip: 'rapid', x: p.x, y: p.y, z: top + 1 });
      h.push({ tip: 'plunge', x: p.x, y: p.y, z: dip, f: fp });          // merkeze dibe in
      h.push({ tip: 'feed', x: p.x + R, y: p.y, z: dip, f: fc });        // diş çapına radyal yanaş
      for (let i = 1; i <= seg; i++) { const a = (i / N) * Math.PI * 2; const zz = dip + (top - dip) * (i / seg); h.push({ tip: 'feed', x: p.x + Math.cos(a) * R, y: p.y + Math.sin(a) * R, z: zz, f: fc, arc: { cx: p.x, cy: p.y, cw: false } }); }   // dipten yukarı helis (G03 + Z)
      h.push({ tip: 'feed', x: p.x, y: p.y, z: top, f: fc });            // merkeze geri çekil
      h.push({ tip: 'retract', x: p.x, y: p.y, z: guvZ });
    }
  }

  /** T-SLOTTING — her noktada slot derinliğine in, yana aç, tam daire (T-kanal yan kesim). */
  private static tslot(h: ICamHareket[], pts: { x: number; y: number; z: number }[], op: ICamOperasyon, takim: ITakim | undefined, guvZ: number, ustZ: number, altZ: number, fc: number, fp: number): void {
    const tcap = takim?.cap ?? 10;
    const slot = op.geometri.cap || tcap * 1.5;
    const R = Math.max(0.3, slot / 2 - tcap / 2);
    const N = 48;
    for (const p of pts) {
      const top = p.z ?? ustZ; const dip = top - (op.geometri.derinlik ?? (top - altZ));
      h.push({ tip: 'rapid', x: p.x, y: p.y, z: guvZ });
      h.push({ tip: 'rapid', x: p.x, y: p.y, z: top + 1 });
      h.push({ tip: 'plunge', x: p.x, y: p.y, z: dip, f: fp });          // slot Z derinliğine in
      h.push({ tip: 'feed', x: p.x + R, y: p.y, z: dip, f: fc });        // yana aç (T-slot freze yan keser)
      for (let i = 1; i <= N; i++) { const a = (i / N) * Math.PI * 2; h.push({ tip: 'feed', x: p.x + Math.cos(a) * R, y: p.y + Math.sin(a) * R, z: dip, f: fc, arc: { cx: p.x, cy: p.y, cw: false } }); }   // tam daire (slot genişliği, G03)
      h.push({ tip: 'feed', x: p.x, y: p.y, z: dip, f: fc });            // merkeze geri
      h.push({ tip: 'retract', x: p.x, y: p.y, z: guvZ });
    }
  }

  /** CATIA Tool Axis (3+2): tool axis ≠ Z ise frame-local toolpath'i world'e döndürür (u,v=düzlem, w=eksen) + her harekete IJK ekler.
   * Eğik düzlemde dairesel interpolasyon karmaşık olduğundan arc'lar güvenle düz feed'e çevrilir (parça doğruluğu korunur). */
  private static toolAxisDonustur(h: ICamHareket[], op: ICamOperasyon): void {
    const ta = op.takimYonu; if (!ta || !ta.yon) return;
    let wx = ta.yon.x, wy = ta.yon.y, wz = ta.yon.z;
    const wl = Math.hypot(wx, wy, wz); if (wl < 1e-9) return; wx /= wl; wy /= wl; wz /= wl;
    if (Math.abs(wx) < 1e-9 && Math.abs(wy) < 1e-9 && Math.abs(wz - 1) < 1e-6) {   // zaten Z — sadece IJK işaretle
      for (const m of h) { m.i = 0; m.j = 0; m.k = 1; } return;
    }
    // dik baz (Gram-Schmidt): up seç → u = up×w, v = w×u
    let ax = 0, ay = 0, az = 1; if (Math.abs(wz) > 0.9) { ay = 1; az = 0; }
    let ux = ay * wz - az * wy, uy = az * wx - ax * wz, uz = ax * wy - ay * wx;
    const ul = Math.hypot(ux, uy, uz) || 1; ux /= ul; uy /= ul; uz /= ul;
    const vx = wy * uz - wz * uy, vy = wz * ux - wx * uz, vz = wx * uy - wy * ux;
    const o = ta.origin || op.geometri.baslangic as any || { x: 0, y: 0, z: 0 };
    const ox = o.x || 0, oy = o.y || 0, oz = o.z || 0;
    for (const m of h) {
      const lx = m.x, ly = m.y, lz = m.z;
      m.x = ox + ux * lx + vx * ly + wx * lz;
      m.y = oy + uy * lx + vy * ly + wy * lz;
      m.z = oz + uz * lx + vz * ly + wz * lz;
      m.i = wx; m.j = wy; m.k = wz;
      if (m.arc) delete m.arc;   // eğik düzlemde yay → güvenli düz feed
    }
  }
  private static uzunluk(h: ICamHareket[]): number { let u = 0; for (let i = 1; i < h.length; i++) { const a = h[i - 1], b = h[i]; u += Math.hypot(b.x - a.x, b.y - a.y, b.z - a.z); } return u; }
  private static sure(h: ICamHareket[], fc: number): number { let t = 0; for (let i = 1; i < h.length; i++) { const a = h[i - 1], b = h[i]; const d = Math.hypot(b.x - a.x, b.y - a.y, b.z - a.z); const f = b.tip === 'rapid' ? 5000 : (b.f || fc); t += d / f; } return t; }
}
